'use client';

import Link from 'next/link';
import styles from './LeftNavItem.module.css';
import type { LeftNavItemProps } from './LeftNavItem.types';
import { Flair, Separator, Flex, Icon, Text, Tooltip } from '@/_components';
import { getClasses } from '@/_utils/classnames';
import { type FC } from 'react';
import { useRouter } from 'next/navigation';

export const LeftNavItem: FC<LeftNavItemProps> = ({
  className,
  href,
  icon = 'circle',
  isActive,
  isBackToWorkspace,
  isDisabled,
  isHovering,
  isLocked,
  isSeparator,
  isTextOnly = false,
  name,
  notificationCount,
  onClick,
  tooltipText,
}) => {
  const leftNavItemClasses: string = getClasses(
    styles.leftNavItem,
    className,
    'flex flex-y-center gap-3',
    {
      [styles.leftNavItemActive]: isActive,
      [styles.leftNavItemDisabled]: isDisabled,
    },
  );

  const onLinkClick = (event: any) => {
    event?.preventDefault?.();

    return onClick?.(event);
  };
  const { push } = useRouter();
  const onBack = () => push('/');

  const backClasses: string = getClasses(styles.leftNavItem__back, {
    [styles.leftNavItem__backNormal]: !isHovering,
  });

  if (isBackToWorkspace) {
    return (
      <Flex className={backClasses} isCentered={true} gap={4} onClick={onBack}>
        <Icon size={16} name="caret-left" color="currentcolor" />
        <Text
          type="small"
          color="currentcolor"
          fontWeight="medium"
          noWrap={true}
        >
          Back to Workspace
        </Text>
      </Flex>
    );
  }

  if (isSeparator && !!name) {
    return (
      <Flex className={styles.leftNavItem__separator} isYCentered={true}>
        <Text type="tiny" color="secondary" fontWeight="medium">
          {name}
        </Text>
      </Flex>
    );
  }

  if (isSeparator) {
    return <Separator isLight={true} />;
  }

  const leftNavItemBase = (
    <>
      <Flex gap={6} isYCentered={true} className={styles.leftNavItem__title}>
        {!isTextOnly && <Icon name={icon} size={20} color="currentcolor" />}
        <Text type="small" color="currentcolor" isEllipsis={true}>
          {name}
        </Text>
      </Flex>
      {isLocked && <Icon name="lock-key" size={16} />}
      {!!notificationCount && (
        <Flair
          className={styles.leftNavItem__flair}
          count={notificationCount}
        />
      )}
    </>
  );

  if (isDisabled && tooltipText) {
    return (
      <Tooltip placement="right">
        <Tooltip.Trigger>
          <Flex className={leftNavItemClasses}>{leftNavItemBase}</Flex>
        </Tooltip.Trigger>
        <Tooltip.Content>
          <Text type="small" color="currentcolor" isEllipsis={true}>
            {tooltipText}
          </Text>
        </Tooltip.Content>
      </Tooltip>
    );
  }

  if (href) {
    return (
      <Link
        className={leftNavItemClasses}
        href={href!}
        data-testid={
          name === 'Documents'
            ? 'LeftNavItem-documents-link'
            : name === 'Messages'
              ? 'LeftNavItem-messages-link'
              : name === 'Dashboard'
                ? 'LeftNavItem-dashboard-link'
                : name === 'Collect'
                  ? 'LeftNavItem-collect-link'
                  : name === 'Entities'
                    ? 'LeftNavItem-entities-link'
                    : name === 'Users'
                      ? 'LeftNavItem-users-link'
                      : name === 'Deliver'
                        ? 'LeftNavItem-deliver-link'
                        : name === 'Connect'
                          ? 'LeftNavItem-connect-link'
                          : undefined
        }
      >
        {leftNavItemBase}
      </Link>
    );
  }

  return (
    <Flex className={leftNavItemClasses} onClick={onLinkClick}>
      {leftNavItemBase}
    </Flex>
  );
};
